import type { CastPlaybackQueue } from "./CastController.types";

export const CAST_PRELOAD_TIME_SECONDS = 30;
export const CAST_CONTROL_NAMESPACE = "urn:x-cast:com.recitalmaster.control";

export type CastSenderDevice = "phone" | "tablet" | "Mac" | "computer";

export type CastAudioTrackDescriptor = {
  castLanguage: string;
  id: string;
  label: string;
  manifestFile: string;
};

export type CastQueueItemData = {
  artworkUrl: string | null;
  contentUrl: string;
  customData: {
    audioTracks: CastAudioTrackDescriptor[];
    danceId: string;
    performanceId: string;
    queueId: string;
    queueIndex: number;
    queueLoadKey: string;
    videoSources?: {
      h264: string;
      hevc?: string;
    };
    urlExpiresAt?: number;
    urlIssuedAt: number;
  };
  duration: number;
  startTime?: number;
  subtitle: string;
  title: string;
};

/**
 * Sender-neutral source of truth for a Recital Master receiver queue. Native
 * and web only translate this model into their Cast SDK's object shapes.
 */
export function buildCastQueue(
  queue: CastPlaybackQueue,
  startTime: number,
  selectedAudioTrack: string | null,
  senderDevice: CastSenderDevice
) {
  const urlIssuedAt = Date.now();
  const items: CastQueueItemData[] = queue.items.map((item, queueIndex) => {
    const audioTracks = item.audioTracks.map((track) => ({
      castLanguage:
        track.id === "audience"
          ? "en-x-rm-audience"
          : track.id === "clean"
            ? "en-x-rm-clean"
            : track.language,
      id: track.id,
      label: track.label,
      manifestFile: track.hlsPath.split("/").pop()!
    }));
    return {
      artworkUrl: item.artworkUrl,
      contentUrl: item.sourceUrl!,
      customData: {
        audioTracks,
        danceId: item.id,
        performanceId: item.performanceId,
        queueId: queue.id,
        queueIndex,
        queueLoadKey: queue.loadKey,
        ...(item.videoSources ? { videoSources: item.videoSources } : {}),
        urlIssuedAt,
        ...(item.urlExpiresAt !== null ? { urlExpiresAt: item.urlExpiresAt } : {})
      },
      duration: item.duration,
      ...(queueIndex === queue.currentIndex ? { startTime } : {}),
      subtitle: item.subtitle,
      title: item.title
    };
  });

  return {
    customData: {
      ...(queue.receiverBadge ? { receiverBadge: queue.receiverBadge } : {}),
      recitalName: queue.recitalName,
      selectedAudioTrack,
      senderDevice,
      urlIssuedAt
    },
    id: queue.id,
    items,
    startIndex: queue.currentIndex,
    startTime
  };
}
